/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { plainToInstance } from 'class-transformer'
import { FormikState } from 'formik'

import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useAlert } from '@hooks/useAlert'
import { useTab } from '@components/Tabs/Tabs'
import { useAuthentication } from '@hooks/useAuthentication'
import { WriterEnum } from '@interfaces/WriterEnum'
import { AffiliationEnum } from '@interfaces/AffiliationEnum'
import { ResidenceEnum } from '@interfaces/ResidenceEnum'
import { CertificationTaxEnum } from '@interfaces/CertificationTaxEnum'
import { WriterStatusEnum } from '@interfaces/WriterStatusEnum'
import { WriterFormType } from '@models/writer/WriterFormType'
import { UserType } from '@models/user/UserType'
import { S3_URL } from '@configs/config'
import { OptionType } from '@interfaces/OptionType'
import { useConsent } from '@hooks/useConsent'
import { useWriterAction } from '@hooks/writer/useWriterAction'
import { UpgradeToWriterProps } from './interface'

const defaultValues: WriterFormType = {
  id: undefined,
  type: WriterEnum.INDIVIDUAL,
  affiliation: AffiliationEnum.INDIVIDUAL,
  fullName: '',
  idNumber: '',
  email: '',
  phoneNumber: '',
  companyName: '',
  taxId: '',
  companyCertificateImgPath: {
    url: '',
  },
  vatRegistrationCertificateImgPath: {
    url: '',
  },
  coordinatorFullName: '',
  coordinatorEmail: '',
  coordinatorPhoneNumber: '',
  accountingFullName: '',
  accountingEmail: '',
  accountingPhoneNumber: '',
  residence: ResidenceEnum.LOCAL,
  country: '',
  address: '',
  postcode: '',
  province: '',
  district: '',
  subDistrict: '',
  idCardImgPath: {
    url: '',
  },
  sendResidence: ResidenceEnum.LOCAL,
  sendCountry: '',
  sendAddress: '',
  sendPostcode: '',
  sendProvince: '',
  sendDistrict: '',
  sendSubDistrict: '',
  isSameMainAddress: false,
  certificateCountry: '',
  certificateAddress: '',
  certificatePostcode: '',
  certificateProvince: '',
  certificateDistrict: '',
  certificateSubDistrict: '',
  certificationTax: CertificationTaxEnum.ONLINE,
  bankAccount: {
    id: undefined,
    bankName: '',
    bankCode: '',
    bankBranch: '',
    bankBranchCode: '',
    bankProvince: '',
    accountNumber: '',
    accountName: '',
    accountType: '',
    bankCoverImgPath: {
      url: '',
    },
  },
  consents: [],
  aboardAddress: '',
  aboardPostcode: '',
  aboardSendAddress: '',
  aboardSendPostcode: '',
  aboardIdCardImgPath: {
    url: '',
  },
  aboardCertificateAddress: '',
  aboardCertificatePostcode: '',
  validateNumberId: false,
  validateTaxId: false,
}

export function withUpgradeToWriter(Component: React.FC<UpgradeToWriterProps>) {
  function WithUpgradeToWriter() {
    const writerClient = useWriterAction()
    const queryClient = useQueryClient()
    const alert = useAlert()
    const { activeTab, handleUpdateActiveTab } = useTab()
    const { user, canFetchApi } = useAuthentication()
    const { data: latestConsent } = useConsent()
    const confirmModal = useModal({ modal: 'confirm' }, true)
    const loadingModal = useModal({ modal: 'loading' }, true)
    const confirmLeavePageModal = useModal({ modal: 'confirmLeavePage' }, true)
    const inProgressModal = useModal({ modal: 'inProgress' }, true)
    const termsOfServiceModal = useModal(
      { modal: 'writerTermsOfService' },
      true
    )
    const [countryOptions, setCountryOptions] = useState<OptionType[]>([])

    const isInProgress =
      user.writerRequest?.status === WriterStatusEnum.CREATED_PENDING ||
      user.writerRequest?.status === WriterStatusEnum.EDITED_PENDING

    useEffect(() => {
      const getCountries = async () => {
        fetch(`${S3_URL}/countries.json`)
          .then(response => response.json())
          .then(jsonData => {
            setCountryOptions(jsonData)
          })
          .catch(error => {
            alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
          })
      }
      getCountries()
    }, [])

    useEffect(() => {
      if (isInProgress && activeTab === 'writer') {
        inProgressModal.show({
          title: 'อยู่ระหว่างรอตรวจสอบเอกสารการสมัครเป็นนักเขียน',
          onContinue: () => handleUpdateActiveTab('myWriting'),
        })
      }
    }, [isInProgress, activeTab])

    if (!latestConsent || !canFetchApi) {
      return <></>
    }

    const initialValues = plainToInstance(WriterFormType, user.writerRequest)
    if (user.writerRequest?.id && !isInProgress) {
      initialValues.consents = [
        {
          id: latestConsent.id,
          checked:
            !!initialValues.consents.find(c => c.id === latestConsent.id) &&
            user.writerRequest?.status === WriterStatusEnum.APPROVED,
        },
      ]
    } else {
      defaultValues.consents = [
        {
          id: latestConsent.id,
          checked: false,
        },
      ]
    }

    async function handleSaveWriter(form: WriterFormType) {
      try {
        loadingModal.show()
        let newData: any = {}
        if (user.writerRequest?.id) {
          newData = await writerClient.updateWriter(form)
        } else {
          newData = await writerClient.createWriter(form)
        }

        if (newData) {
          queryClient.setQueryData<UserType>('my-profile', (oldData: any) => {
            return {
              ...oldData,
              writerRequest: newData,
            }
          })
          alert.success('ส่งข้อมูลสำเร็จ')
        }
      } catch (error) {
        alert.error('เกิดข้อผิดพลาด ไม่สามารถส่งข้อมูลได้')
      } finally {
        loadingModal.hide()
      }
    }

    function showConfirmModal(value: WriterFormType) {
      confirmModal.show({
        content: (
          <div className='flex flex-col items-center text-secondary mb-[20px] desktop-tablet:w-[400px]'>
            <h1 className='font-mitr text-[24px] font-medium dark:text-white-2'>
              ยืนยันการส่งข้อมูลหรือไม่
            </h1>
            <div className='text-center text-sm font-light mt-[10px] max-w-[340px] dark:text-white-2'>
              ระหว่างตรวจสอบข้อมูล จะไม่สามารถแก้ไขข้อมูลได้
            </div>
          </div>
        ),
        onConfirm: () => {
          handleSaveWriter(value)
        },
        onClose: () => confirmModal.hide(),
      })
    }

    async function handleSubmitForm(value: WriterFormType) {
      if (user.writerRequest?.status === WriterStatusEnum.APPROVED) {
        if (latestConsent) {
          const { id, name, content } = latestConsent
          const consent = user?.writerRequest?.writersConsents?.find(
            item => item.consent.id === id
          )

          termsOfServiceModal.show({
            title: name,
            content,
            submittedDate: consent?.createdAt || '',
            showApproveAction: true,
            onApprove: () => showConfirmModal(value),
          })
        }
      } else {
        showConfirmModal(value)
      }
    }

    function handleLeavePage(
      resetForm: (nextState?: Partial<FormikState<WriterFormType>>) => void
    ) {
      confirmLeavePageModal.show({
        onConfirm: () => {
          resetForm({ values: initialValues })
          handleUpdateActiveTab('myWriting')
          confirmLeavePageModal.hide()
        },
        onCancel: () => {
          confirmLeavePageModal.hide()
        },
      })
    }

    const componentProps = {
      initialValues:
        user.writerRequest?.id && !isInProgress ? initialValues : defaultValues,
      countryOptions,
      status: user.writerRequest?.status,
      rejectedReason: user.writerRequest?.rejectedReason,
      isInProgress,
      firstUpgradeToWriter: !user.writerRequest?.writer,
      latestConsent,
      handleSubmitForm,
      handleLeavePage,
    }
    return <Component {...componentProps} />
  }

  return WithUpgradeToWriter
}
